import {
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Popper as MuiPopper,
  Paper,
  PaperProps,
  StandardTextFieldProps,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ArrowDownward, ArrowUpward, KeyboardReturn } from '@material-ui/icons';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteProps,
} from '@material-ui/lab';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { isEqual } from 'es-toolkit/compat';
import { ReactNode, forwardRef, useMemo } from 'react';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { Geocoding } from 'shared/geo/GeoHelpers';
import { useGeoFieldProps } from 'shared/geo/MapboxGeoField';
import { formatAddressLine } from 'shared/helpers/AddressHelpers';
import { trackEvent } from 'shared/helpers/Analytics';
import {
  PostingSearchCriteriaDTO,
  PostingSearchCriteriaVenueDTO,
  composeSavedSearchTitle,
  mapGeocodeToVenue,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { joinStrings } from 'shared/utils/StringUtils';
import styled from 'styled-components';
import { USA_REGIONS, formatRegions } from '../RegionsHelper';
import {
  generateHash,
  useRecentSearches,
} from './PostingRecentSearchesStorage';

const useStyles = makeStyles((theme) => ({
  popperDisablePortal: { zIndex: theme.zIndex.modal + 1 },
}));

const FooterContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px 16px;
  background-color: ${ColorDynamic.Silver200};
`;

const Key = styled.div`
  padding: 2px;
  border-radius: 4px;
  border: 1px solid ${Color.Silver400};
`;

const Popper = styled(MuiPopper).attrs({ placement: 'bottom-start' })`
  min-width: 450px;
  z-index: ${({ theme }) => theme.zIndex.modal + 1};
`;

function AutocompleteFooter() {
  const isDesktop = useResponsiveValue(false, false, true);

  if (!isDesktop) {
    return null;
  }

  return (
    <FooterContainer>
      <Key>
        <ArrowUpward fontSize="inherit" htmlColor={Color.Dark100} />
      </Key>
      <Key>
        <ArrowDownward fontSize="inherit" htmlColor={Color.Dark100} />
      </Key>
      <Typography color="textSecondary">Navigate</Typography>

      {/*  adds empty space */}
      <div />

      <Key>
        <KeyboardReturn fontSize="inherit" htmlColor={Color.Dark100} />
      </Key>
      <Typography color="textSecondary">Select</Typography>
    </FooterContainer>
  );
}

function MapBoxPaperComponent({ children, ...props }: PaperProps) {
  return (
    <Paper {...props}>
      {children}
      <AutocompleteFooter />
    </Paper>
  );
}

const StartIcon = styled.div`
  display: flex;
  align-items: center;
  position: absolute;

  top: 0;
  bottom: 0;
  left: 8px;
`;

const MultipleGeoFieldText = styled(TextField)`
  /* Inline input next to tags rather than next line on focus */
  & .MuiInputBase-input:not(:focus) {
    min-width: auto;
  }

  &[data-start-icon='true'] .MuiOutlinedInput-adornedStart {
    padding-left: 40px;
  }
`;

function formatGeocoding({
  postcode,
  place,
  region_short,
  locality,
  country,
}: Geocoding) {
  return joinStrings(
    ', ',
    locality,
    place,
    joinStrings(' ', region_short, postcode),
    country === 'United States' ? 'USA' : country,
  );
}

function formatVenue(venue: PostingSearchCriteriaVenueDTO) {
  return formatAddressLine(venue.zip, venue.city, venue.state?.toUpperCase());
}

function formatVenues(venues: PostingSearchCriteriaVenueDTO[]) {
  if (venues.length === 0) {
    return 'Anywhere';
  }

  return venues.map(formatVenue).join('; ');
}

function useAutocompleteProps(value?: PostingSearchCriteriaVenueDTO[]) {
  const autocompleteValue = useMemo<SearchAutocompleteOption[]>(() => {
    if (!value) {
      return [];
    }
    return value.map((item) => ({
      type: 'value',
      text: formatVenue(item),
      value: item,
    }));
  }, [value]);

  const geoFieldProps = useGeoFieldProps({
    types: ['place', 'region', 'postcode', 'locality'],
  });
  const recentSearches = useRecentSearches();
  const options: SearchAutocompleteOption[] = useMemo(() => {
    const result: SearchAutocompleteOption[] = [];

    if (geoFieldProps.options.length === 0) {
      for (const [_key, search] of recentSearches) {
        result.push({
          type: 'recent',
          value: search,
          text: composeSavedSearchTitle({
            ...search,
            pickup_venues: [],
            delivery_venues: [],
          }),
        });
      }
    }

    for (const item of geoFieldProps.options) {
      result.push({ type: 'search', value: item, text: formatGeocoding(item) });
    }

    for (const region of USA_REGIONS) {
      result.push({
        type: 'region',
        text: region.region,
        value: region.values.map((state) => ({ state: state.toLowerCase() })),
      });
    }

    return result;
  }, [geoFieldProps.options, recentSearches]);

  return {
    ...(geoFieldProps as AutocompleteProps<
      SearchAutocompleteOption,
      true,
      true,
      false
    >),
    options,
    value: autocompleteValue,
  };
}

interface VenueOption {
  type: 'value';
  text: string;
  value: PostingSearchCriteriaVenueDTO;
}

interface SearchOption {
  type: 'search';
  text: string;
  value: Geocoding;
}

interface RegionOption {
  type: 'region';
  text: string;
  value: PostingSearchCriteriaVenueDTO[];
}

interface RecentOption {
  type: 'recent';
  text: string;
  value: PostingSearchCriteriaDTO;
}

type SearchAutocompleteOption =
  | SearchOption
  | RegionOption
  | RecentOption
  | VenueOption;

export interface PostingSearchCriteriaVenueFieldProps extends Omit<
  AutocompleteProps<SearchAutocompleteOption, true, true, false>,
  'value' | 'onChange' | 'multiple' | 'options' | 'renderTags' | 'renderInput'
> {
  TextFieldProps?: StandardTextFieldProps;
  startIcon?: ReactNode;
  value?: PostingSearchCriteriaVenueDTO[];
  searchCriteria: PostingSearchCriteriaDTO;
  onRecentClick: (value: PostingSearchCriteriaDTO) => void;
  onChange?: (
    value: PostingSearchCriteriaVenueDTO[],
    reason: AutocompleteChangeReason,
  ) => void;
}

export const PostingSearchCriteriaVenueField = forwardRef(
  (
    {
      value,
      searchCriteria,
      onChange,
      onRecentClick,
      TextFieldProps,
      startIcon,
      ...rest
    }: PostingSearchCriteriaVenueFieldProps,
    ref,
  ) => {
    const placeholder = !value?.length
      ? TextFieldProps?.placeholder
      : value.length === 1
        ? 'Search'
        : '';

    const classes = useStyles();
    const autocompleteProps = useAutocompleteProps(value);
    const selectedRegion = useMemo(() => formatRegions(value || []), [value]);
    const currentSearchHash = useMemo(
      () => generateHash(searchCriteria),
      [searchCriteria],
    );

    return (
      <Box position="relative">
        <Autocomplete<SearchAutocompleteOption, true, true, false>
          {...rest}
          {...autocompleteProps}
          ref={ref}
          openOnFocus={true}
          multiple={true}
          PopperComponent={Popper}
          classes={classes}
          // Autocomplete overlaps over input element on Webview while using portal
          disablePortal={isWebView}
          groupBy={(option) => option.type}
          onChange={(_event, changeValue, reason) => {
            if (!Array.isArray(changeValue) || changeValue.length === 0) {
              onChange?.([], reason);
              return;
            }

            const recentOption = changeValue.find((x) => x?.type === 'recent');
            if (recentOption?.type === 'recent') {
              onRecentClick(recentOption.value);
              return;
            }

            onChange?.(
              changeValue.flatMap((item) => {
                if (!item) {
                  return [];
                }

                if (item.type === 'region') {
                  return item.value;
                }

                if (item.type === 'search') {
                  return mapGeocodeToVenue(item.value);
                }

                if (item.type === 'value') {
                  return item.value;
                }

                return [];
              }),
              reason,
            );
          }}
          renderTags={(items: SearchAutocompleteOption[], getTagProps) =>
            items.map((item: VenueOption, index) => (
              <Chip key={index} label={item.text} {...getTagProps({ index })} />
            ))
          }
          renderOption={(option) => {
            if (option.type === 'region') {
              return (
                <Columns align="top" space="xxsmall">
                  <Column width="content">
                    <Box width="108px">
                      <Typography variant="body1" align="left">
                        {option.text}
                      </Typography>
                    </Box>
                  </Column>

                  <Column>
                    <Typography
                      color="textSecondary"
                      align="left"
                      noWrap={true}
                    >
                      {option.value
                        .map((x) => x.state?.toUpperCase())
                        .join(', ')}
                    </Typography>
                  </Column>
                </Columns>
              );
            }

            if (option.type === 'recent') {
              const title = composeSavedSearchTitle({
                ...option.value,
                pickup_venues: [],
                delivery_venues: [],
              });
              return (
                <Box maxWidth="100%">
                  <div
                    onClick={() => {
                      trackEvent('SLB: Clicked Recent Search', {
                        search: title,
                        origin: formatVenues(option.value.pickup_venues),
                        destination: formatVenues(option.value.delivery_venues),
                      });
                    }}
                  >
                    <Typography variant="body1" align="left" noWrap={true}>
                      {formatVenues(option.value.pickup_venues)}
                      {' -> '}
                      {formatVenues(option.value.delivery_venues)}
                    </Typography>

                    <Typography
                      noWrap={true}
                      align="left"
                      color="textSecondary"
                    >
                      {title}
                    </Typography>
                  </div>
                </Box>
              );
            }

            return option.text;
          }}
          renderGroup={({ key, group, children }) => {
            const isFirstGroup = String(key) === '0';

            if (group === 'search') {
              return <div key={key}>{children}</div>;
            }

            return (
              <div key={key}>
                {group === 'region' && !isFirstGroup && <Divider />}

                <Box
                  paddingTop="xsmall"
                  paddingBottom="xsmall"
                  paddingLeft="small"
                  paddingRight="small"
                >
                  <Typography variant="h5" color="textSecondary">
                    {group === 'region' ? 'Regions' : 'Recent Searches'}
                  </Typography>
                </Box>

                {children}
              </div>
            );
          }}
          getOptionSelected={(selectedOption, selectedValue) => {
            if (
              selectedOption &&
              selectedOption.type === 'search' &&
              selectedValue &&
              selectedValue.type === 'value'
            ) {
              return isEqual(
                mapGeocodeToVenue(selectedOption.value),
                selectedValue.value,
              );
            }

            if (selectedOption.type === 'region') {
              return selectedRegion.includes(selectedOption.text);
            }

            if (selectedOption.type === 'recent') {
              return generateHash(selectedOption.value) === currentSearchHash;
            }

            return false;
          }}
          filterSelectedOptions={true}
          filterOptions={(filterOptions, state) => {
            return filterOptions.filter((option) => {
              if (option.type === 'region') {
                return option.text.toLowerCase().includes(state.inputValue);
              }

              return true;
            });
          }}
          ListboxProps={{ style: { maxHeight: '60vh' } }}
          PaperComponent={MapBoxPaperComponent}
          renderInput={(params) => (
            <MultipleGeoFieldText
              {...params}
              {...TextFieldProps}
              placeholder={placeholder}
              data-start-icon={String(!!startIcon)}
              InputProps={{
                ...TextFieldProps?.InputProps,
                ...params.InputProps,
                startAdornment: (
                  <>
                    {startIcon && <StartIcon>{startIcon}</StartIcon>}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: autocompleteProps.loading ? (
                  <InputAdornment position="end">
                    <CircularProgress color="inherit" size={20} />
                  </InputAdornment>
                ) : (
                  params.InputProps.endAdornment
                ),
              }}
              variant="outlined"
              fullWidth={true}
            />
          )}
        />
      </Box>
    );
  },
);

PostingSearchCriteriaVenueField.displayName = 'PostingSearchCriteriaVenueField';
